import {
    EuiBasicTableColumn,
    EuiButtonIcon,
    EuiFlexGroup,
    EuiFlexItem,
    EuiFormRow,
    EuiInMemoryTable,
    EuiPanel,
    EuiSpacer,
    EuiStat,
} from "@elastic/eui";
import {NumberToCurrency} from "../../utils/Formatters";
import {GetMonthByNumber, GetMonthNumber, Month, MonthYearPicker,} from "../time/MonthYearPicker";
import {useEffect, useRef, useState} from "react";
import moment, {Moment} from "moment";
import {BillingOrderType, Commission} from "../../api/entities/Commission";
import {Plan} from "../../api/entities/Plan";
import {useGetBusinessesQuery} from "../../api/rtkQueryApi/BusinessesApi";
import {useGetCurrentCommissionRate} from "components/commissions/useGetCurrentCommissionRate";
import {UserCommissionData} from "pages/accounting/AccountingCommissionsSummary";
import {AddonDetails} from "../../api/entities/Addon";

export interface ICommissionsSummary {
    commissions: Commission[];
    plans: Plan[];
    isLoading?: boolean;
    selectedUser?: UserCommissionData;
}

interface SaleSummary {
    businessName: string;
    itemName: string;
    completedMonths: number;
    durationMonths: number;
    invoicePaidDate: Moment;
    startDate: Moment;
    discount: number;
    amountPaid: number;
    totalContractValue: number;
    ncr: number;
    commissionPercentage: number;
    commission: number;
}

export const SalesSummary: React.FC<ICommissionsSummary> = (props) => {
    const now = useRef(moment());
    const {commissionRates, commissionRatesIsFetching} =
        useGetCurrentCommissionRate(now.current, props.selectedUser?.email);

    const [minDate, setMinDate] = useState<moment.Moment>();
    const [selectedMonth, setSelectedMonth] = useState<Month>(
        GetMonthByNumber(now.current.month()),
    );
    const [selectedYear, setSelectedYear] = useState<number>(now.current.year());

    const [thisMonthPlansSold, setThisMonthPlansSold] = useState<Plan[]>([]);

    const [filteredCommissions, setFilteredCommissions] = useState<Commission[]>(
        [],
    );
    const [thisMonthExpectedCommission, setThisMonthExpectedCommission] =
        useState<number>(0);
    const [thisMonthTotalNCR, setThisMonthTotalNCR] = useState<number>(0);

    const [thisYearTotalContractValue, setThisYearTotalContractValue] =
        useState<number>(0);

    const [addonMonthlyCommission, setAddonMonthlyCommission] = useState<number>(0);

    const [salesSummaries, setSalesSummaries] = useState<SaleSummary[]>([]);

    const getAllBusinessesQuery = useGetBusinessesQuery();

    const onMonthYearPickerChange = (month: Month, year: number) => {
        setSelectedMonth(month);
        setSelectedYear(year);
    };

    useEffect(() => {
        if (!getAllBusinessesQuery.data) return;

        const businesses = getAllBusinessesQuery.data;
        const summaries: SaleSummary[] = [];
        const thisMonthPlans: Plan[] = []
        const thisMonthAddonCommissions: Commission[] = []
        filteredCommissions.forEach((commission) => {
            if (commission.referenceType === "AddonDetails") {
                const plan = props.plans.find((p) =>
                    p.addonDetailsList.find((a) => a.id === commission.referenceId),
                );
                const addon = plan?.addonDetailsList.find(
                    (a) => a.id === commission.referenceId,
                );

                if(addon)
                {
                    thisMonthAddonCommissions.push(commission);
                }

                const business = businesses.find((b) => b.id === plan?.businessId);

                summaries.push({
                    businessName: business?.name ?? "",
                    itemName: commission.name ?? "",
                    completedMonths: addon?.schedule?.intervalsCompleted ?? 0,
                    durationMonths: addon?.schedule?.totalIntervals ?? 0,
                    invoicePaidDate: moment(commission.invoicePaidDate),
                    startDate: moment(addon?.schedule?.eventStartDate),
                    discount: 0,
                    amountPaid: commission.orderAmount,
                    totalContractValue: addon?.schedule
                        ? addon.schedule.totalIntervals * commission.orderAmount
                        : 0,
                    ncr: commission.netCommissionableRevenue,
                    commissionPercentage: commission.commissionRate * 100,
                    commission: commission.commissionAmount,
                });
            } else {
                const plan = props.plans.find((p) => p.id === commission.referenceId);

                if(!plan)
                {
                    return;
                }

                if(commission.orderType === BillingOrderType.Activation) {
                    thisMonthPlans.push(plan);
                }
                const business = businesses.find((b) => b.id === plan?.businessId);

                summaries.push({
                    businessName: business?.name ?? "",
                    itemName: commission.name ?? "",
                    completedMonths: plan?.schedule.intervalsCompleted ?? 0,
                    durationMonths: plan?.schedule.totalIntervals ?? 0,
                    invoicePaidDate: moment(commission.invoicePaidDate),
                    startDate: moment(plan?.schedule.eventStartDate),
                    discount: plan?.discount ?? 0,
                    amountPaid: commission.orderAmount,
                    totalContractValue: plan
                        ? plan.schedule.totalIntervals * plan.productDetails.pricePerMonth
                        : 0,
                    ncr: commission.netCommissionableRevenue,
                    commissionPercentage: commission.commissionRate * 100,
                    commission: commission.commissionAmount,
                });
            }
        })
        setAddonMonthlyCommission(thisMonthAddonCommissions.reduce((acc, commission) => acc + commission.orderAmount, 0))
        setSalesSummaries(summaries);
        setThisMonthPlansSold(thisMonthPlans);

        const monthNCR = filteredCommissions.filter(c => c.referenceType !== "AddonDetails").reduce(
            (acc, commission) => acc + commission.netCommissionableRevenue,
            0,
        );
        setThisMonthTotalNCR(monthNCR);
    }, [filteredCommissions, getAllBusinessesQuery.data, props.plans]);

    useEffect(() => {
        const minimumDate = moment.min(
            props.commissions.map((c) => moment(c.invoicePaidDate)),
        );
        setMinDate(minimumDate);
        const filCommissions = props.commissions.filter(
            (c) =>
                moment(c.invoicePaidDate).year() == selectedYear &&
                moment(c.invoicePaidDate).month() == GetMonthNumber(selectedMonth),
        );
        setFilteredCommissions(filCommissions);
    }, [props.commissions, selectedYear, selectedMonth]);

    useEffect(() => {
        let totalContractValue = 0;
        const thisYearCommissions = props.commissions.filter(
            (c) => moment(c.invoicePaidDate).year() === selectedYear
        );
        const commissionsWithDedupedReferences = thisYearCommissions.filter(
            (commission, index, self) =>
                self.findIndex((c) => c.referenceId === commission.referenceId) === index,
        );

        commissionsWithDedupedReferences.forEach((commission) => {
            if (commission.referenceType === "AddonDetails") {
                const plan = props.plans.find((p) =>
                    p.addonDetailsList.find((a) => a.id === commission.referenceId),
                );
                const addon = plan?.addonDetailsList.find(
                    (a) => a.id === commission.referenceId,
                );

                totalContractValue += addon?.schedule
                    ? addon.schedule.totalIntervals * commission.orderAmount
                    : 0;
            } else {
                const plan = props.plans.find((p) => p.id === commission.referenceId);
                totalContractValue += plan
                    ? plan.schedule.totalIntervals * plan.productDetails.pricePerMonth
                    : 0;
            }
        });
        setThisYearTotalContractValue(totalContractValue);
    }, [props.commissions, props.plans]);

    useEffect(() => {
        if(!commissionRates)
        {
            return
        }
        const selectedMoment = moment()
            .year(selectedYear)
            .month(GetMonthNumber(selectedMonth));
        const lastMonth = selectedMoment.clone().subtract(1, "month");
        const lastMonthCommissions = props.commissions.filter(
            (c) =>
                moment(c.invoicePaidDate).year() == lastMonth.year() &&
                moment(c.invoicePaidDate).month() == lastMonth.month() &&
                moment(c.invoicePaidDate).day() > moment().day(),
        );
        const thisMonthCommissions = props.commissions.filter(
            (c) =>
                moment(c.invoicePaidDate).year() == selectedYear &&
                moment(c.invoicePaidDate).month() == GetMonthNumber(selectedMonth),
        );

        const lastMonthCommissionsTotal = lastMonthCommissions.reduce(
            (acc, commission) => acc + commission.commissionAmount,
            0,
        );
        const thisMonthCommissionsTotal = thisMonthCommissions.reduce(
            (acc, commission) => {
                const foundCommissionPlanRate = commissionRates?.find( rate => rate.commissionPlanId === commission.commissionPlanId)?.commissionRate ?? 0
                const expectedCommission = foundCommissionPlanRate * commission.netCommissionableRevenue
                return acc + expectedCommission
            },0,
        );
        setThisMonthExpectedCommission(
            lastMonthCommissionsTotal + thisMonthCommissionsTotal,
        );
    }, [props.commissions, commissionRates]);

    const getColumns: () => EuiBasicTableColumn<SaleSummary>[] = () => [
        {
            name: "Business",
            field: "businessName",
            sortable: true,
        },
        {
            name: "Product",
            field: "itemName",
            sortable: true,
        },
        {
            name: "Duration",
            render: (summary: SaleSummary) =>
                `${summary.completedMonths}/${summary.durationMonths}`,
            sortable: (summary: SaleSummary) => summary.completedMonths,
        },
        {
            name: "Monthly NCR",
            field: "ncr",
            sortable: true,
            render: (ncr: number) => NumberToCurrency(ncr),
        },
        {
            name: "Total Contract Value",
            field: "totalContractValue",
            sortable: true,
            render: (amount: number) => NumberToCurrency(amount),
        },
        {
            name: "Date Added",
            field: "invoicePaidDate",
            sortable: true,
            render: (date: Moment) => moment(date).format("MM/DD/YYYY"),
        }
    ];

    const pagination = {
        initialPageSize: 25,
        pageSizeOptions: [25, 50, 100],
    };

    const downloadCSV = () => {
        const csv = salesSummaries.map((summary) => {
            return {
                Business: summary.businessName,
                Product: summary.itemName,
                Duration: `${summary.completedMonths}/${summary.durationMonths}`,
                "Monthly NCR": summary.ncr,
                "Total Contract Value": summary.totalContractValue,
            };
        });

        const headers = Object.keys(csv[0]).join(",");
        const csvData = csv.map((row) => Object.values(row).join(",")).join("\n");

        const blob = new Blob([`${headers}\n${csvData}`], {type: "text/csv"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${selectedMonth}-${selectedYear}-sales-summary.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (props.isLoading || commissionRatesIsFetching) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <EuiFormRow>
                <MonthYearPicker
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    minDate={minDate}
                    maxDate={moment()}
                    onChange={onMonthYearPickerChange}
                />
            </EuiFormRow>
            <EuiFormRow fullWidth>
                <EuiFlexGroup wrap>
                    <EuiFlexItem grow={false}>
                        <EuiPanel hasShadow={false} hasBorder={true} style={{width: 240}}>
                            <EuiStat
                                description={"Agreements This Month"}
                                title={`${thisMonthPlansSold.length}`}
                                titleSize={"s"}
                            />
                        </EuiPanel>
                    </EuiFlexItem>
                    {commissionRates && commissionRates.length > 0 && ( () => {
                            const commissionRate = commissionRates[0].commissionRate;
                            const displayRate = commissionRate ? `${commissionRate * 100}%` : "N/A";
                            return (
                                <EuiFlexItem grow={false} key={'commissionRate'}>
                                    <EuiPanel
                                        hasShadow={false}
                                        hasBorder={true}
                                        style={{width: 240}}
                                    >
                                        <EuiStat
                                            description={`Current Monthly Rate`}
                                            title={displayRate}
                                            titleSize={"s"}
                                        />
                                    </EuiPanel>
                                </EuiFlexItem>
                            )
                        })()
                    }
                    <EuiFlexItem grow={false}>
                        <EuiPanel hasShadow={false} hasBorder={true} style={{width: 240}}>
                            <EuiStat
                                description={"Monthly Product NCR"}
                                title={`${NumberToCurrency(thisMonthTotalNCR)}`}
                                titleSize={"s"}
                            />
                        </EuiPanel>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiPanel hasShadow={false} hasBorder={true} style={{width: 240}}>
                            <EuiStat
                                description={"Monthly Addon NCR"}
                                title={`${NumberToCurrency(addonMonthlyCommission)}`}
                                titleSize={"s"}
                            />
                        </EuiPanel>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiPanel hasShadow={false} hasBorder={true} style={{width: 240}}>
                            <EuiStat
                                description={"Current Revenue Statement"}
                                title={`${NumberToCurrency(thisMonthExpectedCommission)}`}
                                titleSize={"s"}
                            />
                        </EuiPanel>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiPanel hasShadow={false} hasBorder={true} style={{width: 240}}>
                            <EuiStat
                                description={"Contract Value Sold This Year"}
                                title={`${NumberToCurrency(thisYearTotalContractValue)}`}
                                titleSize={"s"}
                            />
                        </EuiPanel>
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiFormRow>
            <EuiFormRow>
                <EuiButtonIcon
                    iconType={"importAction"}
                    onClick={downloadCSV}
                    title={"Download"}
                />
            </EuiFormRow>
            <EuiSpacer/>
            <EuiInMemoryTable
                items={salesSummaries}
                itemId="id"
                columns={getColumns()}
                pagination={pagination}
                sorting={true}
            />
        </>
    );
};
